import { isNativePlatform } from '../runtime'
import {
  registerEmployerPushNotifications,
  readPushPermission,
  initForegroundPushDisplay,
  PUSH_STATUS,
} from '../../firebase/messaging'
import { NativeNotifications } from './nativeNotifications'
import { refreshThisDevicePushToken } from '../nativeDeviceRegistry'

/**
 * Maps the native plugin's own permission vocabulary onto the SAME
 * PUSH_STATUS enum messaging.js already exports, so a caller (e.g.
 * PushNotificationStatus.js) can branch on one shared set of values
 * regardless of platform — no separate "native status" concept.
 *
 * @param {?string} status 'granted'|'denied'|'prompt' from the plugin.
 * @return {string} one of PUSH_STATUS.
 */
function mapNativePermission(status) {
  switch (status) {
    case 'granted':
      return PUSH_STATUS.READY
    case 'denied':
      return PUSH_STATUS.BLOCKED
    default:
      return PUSH_STATUS.PROMPTABLE
  }
}

/**
 * Read the current native push permission WITHOUT prompting. Mirrors
 * messaging.js's readPushPermission — safe to call on every render.
 *
 * @return {Promise<string>} one of PUSH_STATUS.
 */
async function readNativePushPermission() {
  try {
    const result = await NativeNotifications.readPermission()
    return mapNativePermission(result?.status)
  } catch {
    // The plugin isn't reachable (e.g. a web build synced before `cap sync`
    // registered it natively) — an honest BROKEN, not a false READY/PROMPTABLE.
    return PUSH_STATUS.BROKEN
  }
}

/**
 * Requests the OS notification permission, and if granted, submits
 * whatever installation token the native layer already has for THIS device
 * to `refreshNativePushToken` (#1691's device-registry callable — see
 * nativeDeviceRegistry.js). Also subscribes to the plugin's own
 * `'registration'` event so a LATER OS-driven token rotation (no user
 * action, no call to this function) is resubmitted automatically — ticket
 * requirement 3: "רוטציה מעדכנת את הטוקן הקיים."
 *
 * A device must already be registered via `registerThisDevice` (#1691,
 * called once after login — see src/index.js's native bootstrap) before
 * this can succeed; `refreshNativePushToken` 404s otherwise, which this
 * surfaces as `{status: 'device-not-registered'}` rather than throwing —
 * same "explicit result, not a silent no-op" contract as messaging.js's
 * registerEmployerPushNotifications.
 *
 * @return {Promise<{status: string, token?: string}>}
 */
async function registerNativePushNotifications() {
  let permission
  try {
    permission = await NativeNotifications.requestPermission()
  } catch (error) {
    return { status: 'token-unavailable', error: String(error?.message || error) }
  }

  if (!permission?.granted) {
    // spec.md §12.1 PUSH_PERMISSION_BLOCKED — a user choice, not a fault.
    return { status: 'permission-denied' }
  }

  // Every future rotation flows through the same submit path, registered
  // once. Re-registering the listener on every register() call would stack
  // duplicate handlers; Capacitor's addListener has no "already registered"
  // guard of its own, so this module-level flag is this file's.
  subscribeToTokenRotation()

  let token = null
  try {
    const result = await NativeNotifications.getToken()
    token = result?.token || null
  } catch {
    token = null
  }

  if (!token) {
    // Not a fault: the OS may not have delivered a token yet (async on both
    // platforms) — the 'registration' event subscribed above will submit it
    // the moment it arrives.
    return { status: 'token-pending' }
  }

  return submitToken(token)
}

let rotationSubscribed = false

function subscribeToTokenRotation() {
  if (rotationSubscribed) return
  rotationSubscribed = true
  NativeNotifications.addListener('registration', ({ token }) => {
    if (token) submitToken(token)
  }).catch(() => {})
}

async function submitToken(token) {
  try {
    const result = await refreshThisDevicePushToken(token)
    if (!result) {
      // isNativePlatform() was false, or the device isn't registered yet —
      // nativeDeviceRegistry.js's own refreshThisDevicePushToken returns
      // null for the former; the callable 404s for the latter and this
      // catch below handles it.
      return { status: 'device-not-registered' }
    }
    return { status: 'registered', token }
  } catch (error) {
    return { status: 'device-not-registered', error: String(error?.message || error) }
  }
}

/**
 * Foreground push display. UNLIKE web (where FCM hands the payload to JS via
 * onMessage and the app must call showNotification itself), the OS display
 * path here is entirely NATIVE: iOS's `UNUserNotificationCenterDelegate.
 * willPresent` and Android's `NotificationCompat` call in NativePushPlugin's
 * onMessageReceived/didReceiveRemoteNotification both show the system
 * notification without any JS involvement — see those files' own comments.
 * This still exists (rather than being omitted) so `getPushAdapter()`'s
 * three-method shape stays identical on both platforms; it registers the
 * `'pushReceived'` listener for parity/future use (e.g. an in-app toast)
 * but performs no display of its own — an explicit, documented no-op, not a
 * silent one.
 *
 * @return {Promise<Function>} unsubscribe.
 */
async function initNativeForegroundDisplay() {
  try {
    const handle = await NativeNotifications.addListener('pushReceived', () => {})
    return () => handle.remove()
  } catch {
    return () => {}
  }
}

/**
 * Push capability (#1690, addendum §5.2 row 1: Web = FCM Web/VAPID/service
 * worker; Native = APNs/FCM installation token via NativeNotificationAdapter).
 *
 * The web implementation is unchanged — services/firebase/messaging.js, whose
 * own canUseMessagingBrowserApis() already refuses to run under
 * isNativePlatform(), so the two adapters can never both act on the same
 * install even if a caller forgets to branch here too.
 *
 * The native adapter (#1695) uses NEITHER VAPID nor a service worker — it
 * talks to `NativeNotifications` (src/services/platform/adapters/
 * nativeNotifications.js), the local Capacitor plugin bridge, and stores its
 * token via #1691's `refreshNativePushToken` callable.
 */
export function getPushAdapter() {
  if (isNativePlatform()) {
    return {
      register: registerNativePushNotifications,
      readPermission: readNativePushPermission,
      initForegroundDisplay: initNativeForegroundDisplay,
    }
  }
  return {
    register: registerEmployerPushNotifications,
    readPermission: readPushPermission,
    initForegroundDisplay: initForegroundPushDisplay,
  }
}
